import { useEffect, useState, type FormEvent } from 'react';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type { Stripe } from '@stripe/stripe-js';

import { ModalCloseButton } from './InfoModals';
import { isStripeConfigured } from '../stripeConfig';

export interface PaidScanPaymentSubmission {
  paymentMethodId: string;
  paymentAttemptId: string;
  stripe: Stripe;
}

interface PaidScanPaymentModalProps {
  isOpen: boolean;
  amountUsd?: number;
  planLabel: string;
  isSubmitting: boolean;
  error: string;
  onCancel: () => void;
  onError: (message: string) => void;
  onSubmit: (submission: PaidScanPaymentSubmission) => Promise<void>;
}

const createPaymentAttemptId = (): string => {
  if (typeof window.crypto?.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }

  return `attempt-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
};

const formatAmount = (amountUsd: number | undefined): string => {
  return typeof amountUsd === 'number'
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amountUsd)
    : 'the server-calculated amount';
};

export default function PaidScanPaymentModal({
  isOpen,
  amountUsd,
  planLabel,
  isSubmitting,
  error,
  onCancel,
  onError,
  onSubmit
}: PaidScanPaymentModalProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [isCardComplete, setIsCardComplete] = useState(false);
  const [cardError, setCardError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setIsCardComplete(false);
      setCardError('');
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const cancel = () => {
    if (!isSubmitting) {
      onCancel();
    }
  };

  const submitPayment = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setCardError('');
    onError('');

    if (!isStripeConfigured) {
      onError('Stripe is not configured. Set stripePublishableKey in runtime-config.js and reload the page.');
      return;
    }

    const card = elements?.getElement(CardElement);

    if (!stripe || !card) {
      onError('The secure payment form is still loading. Please try again.');
      return;
    }

    if (!isCardComplete) {
      setCardError('Complete the card details before paying.');
      return;
    }

    const result = await stripe.createPaymentMethod({ type: 'card', card });

    if (result.error || !result.paymentMethod) {
      setCardError(result.error?.message ?? 'Stripe could not create the payment method.');
      return;
    }

    await onSubmit({
      paymentMethodId: result.paymentMethod.id,
      paymentAttemptId: createPaymentAttemptId(),
      stripe
    });
  };

  return (
    <div className="modal-overlay" onClick={cancel}>
      <form
        className="modal-content paid-scan-payment-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="paid-scan-payment-title"
        onClick={(event) => event.stopPropagation()}
        onSubmit={(event) => void submitPayment(event)}
      >
        <div className="modal-header">
          <h2 id="paid-scan-payment-title">Purchase {planLabel.toLowerCase()}</h2>
          <ModalCloseButton onClose={cancel} disabled={isSubmitting} />
        </div>

        <div className="modal-body paid-scan-payment-body">
          <div className="paid-scan-payment-summary">
            <span>Amount</span>
            <strong>{formatAmount(amountUsd)}</strong>
          </div>
          <p>
            The backend recalculates the final amount before charging. Card details are handled
            securely by Stripe and are never sent to Havu.
          </p>

          {!isStripeConfigured && (
            <p className="pricing-email-error" role="alert">
              Stripe is not configured. Set stripePublishableKey in runtime-config.js and reload the page.
            </p>
          )}

          <label className="field">
            <span>Card details</span>
            <div className="paid-scan-card-field">
              <CardElement
                options={{
                  hidePostalCode: false,
                  style: {
                    base: {
                      color: '#f4f4f5',
                      fontFamily: 'Inter, system-ui, sans-serif',
                      fontSize: '16px',
                      '::placeholder': { color: '#71717a' }
                    },
                    invalid: { color: '#f87171' }
                  }
                }}
                onChange={(event) => {
                  setIsCardComplete(event.complete);
                  setCardError(event.error?.message ?? '');
                }}
              />
            </div>
          </label>

          {(cardError || error) && (
            <p className="pricing-email-error" role="alert">{cardError || error}</p>
          )}
        </div>

        <div className="modal-footer pricing-email-footer">
          <button type="button" className="secondary-button" disabled={isSubmitting} onClick={cancel}>
            Cancel
          </button>
          <button
            type="submit"
            className="primary-button"
            disabled={isSubmitting || !isStripeConfigured || !stripe}
          >
            {isSubmitting
              ? 'Processing payment...'
              : `Pay ${formatAmount(amountUsd)}`}
          </button>
        </div>
      </form>
    </div>
  );
}
